import { DatePicker, Form, message, Typography } from "antd";

import moment from "moment";
import "../submitToBilling.scss";
import { displayDollar } from "utilities/tableUtility";
import {
  disabledDatesPriorMonth,
} from "utilities/dateUtility";
import { useState, useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import BillingApproval from "redux/models/billingApproval";
import K from "utilities/constants";

import {ServerSideDataGrid} from "../../../../common/components/serverSideDataGrid/serverSideDataGrid";

import {DiscountedColumnDefs, NonDiscountedColumnDefs} from "./columns"
import {selectUserId} from "../../../../redux/slices/userSlice";

const { Title } = Typography;
const {
  Invoice: { Type },
} = K;

export default function CreateJobInvoice({
  form,
  invoicePayloadRef,
  pageStates,
  setLoading,
  setPageStates,
}) {
  const userId = useSelector(selectUserId);

  const [totalDiscount, setTotalDiscount] = useState(0);
  const [gridsLoaded, setGridsLoaded] = useState({ discounted: false, nonDiscounted: false });

  const createBillingInvoice = async (values) => {
    setLoading(true);
    const { index, ...payload } = invoicePayloadRef.current;
    payload.dueDate = values.dueDate.format(K.DateFormat.Response);

    const invoicebody = {
      clientId: payload.clientId,
      type: payload.type,
      dueDate: payload.dueDate,
      selectAll: payload.selectAll,
      toggledNodes: payload.submittalBillIds,
    };
    try {
      const res = await BillingApproval.generateInvoice(invoicebody);
      message.success(res.message);
      setPageStates((prev) => ({
        ...prev,
        jobLoading: payload.type === Type.Job,
        isJobDueDateModalVisible: false,
      }));
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const getDiscountedServerSideData = useCallback(
    async (options) => {
      setGridsLoaded((prev) => ({ ...prev, discounted: false }));
      const { clientId, selectAll, submittalBillIds } = invoicePayloadRef.current;
      const res = await BillingApproval.getVolumeBasedBillings(clientId, {
        ...options,
        discountType: 'hires',
      }, {
        selectAll: selectAll ? 1 : 0,
        toggledNodes: submittalBillIds,
      });

      setTotalDiscount(res.meta?.totalDiscount ?? 0);
      setGridsLoaded((prev) => ({ ...prev, discounted: true }));

      return res;
    },
    [],
  );

  const getNonDiscountedServerSideData = useCallback(
    async (options) => {
      setGridsLoaded((prev) => ({ ...prev, nonDiscounted: false }));
      const { clientId, selectAll, submittalBillIds } = invoicePayloadRef.current;
      const res = await BillingApproval.getVolumeBasedBillings(clientId, {
        ...options,
        discountType: 'others',
      }, {
        selectAll: selectAll ? 1 : 0,
        toggledNodes: submittalBillIds,
      });

      setGridsLoaded((prev) => ({ ...prev, nonDiscounted: true }));

      return res;
    },
    [],
  );

  useEffect(() => {
    setLoading(!(gridsLoaded.discounted && gridsLoaded.nonDiscounted));
  }, [gridsLoaded]);

  return (
    <Form layout="vertical" form={form} onFinish={createBillingInvoice}>
      <Form.Item
        label="Invoice Draft Date"
        name="dueDate"
        initialValue={moment()}
        rules={[{ required: true, message: "Please select due date" }]}
      >
        <DatePicker
          disabledDate={disabledDatesPriorMonth}
          placeholder="Due Date"
          format={K.DateFormat.DashUSFormat}
        />
      </Form.Item>

      {invoicePayloadRef.current.type === Type.Job && (
        <div className="text-left">
          <Title level={5} className="mb-0">
            Previous total:{" "}
            {displayDollar(
              invoicePayloadRef.current
                ? pageStates.checkedRows[
                    invoicePayloadRef.current.type === Type.Job
                      ? "jobTotal"
                      : "clientTotal"
                  ]
                : 0,
            )}
          </Title>
          <Title level={5} className="mb-0">
            Total Discount: -{displayDollar(totalDiscount)}
          </Title>
          <div className="text-right">
            <Title level={5} style={{ color: "var(--blue-300)" }} className="mb-0">
              Grand Total:{" "}
              {displayDollar(
                (invoicePayloadRef.current
                  ? pageStates.checkedRows[
                      invoicePayloadRef.current.type === Type.Job
                        ? "jobTotal"
                        : "clientTotal"
                    ]
                  : 0) - totalDiscount,
              )}
            </Title>
          </div>

          <div style={{ textAlign: "left" }}>
            <Title style={{ color: "var(--blue-300)" }} level={5}>
              Discount Eligibles
            </Title>
          </div>
          <ServerSideDataGrid
            pagination
            paginationPageSize={5}
            userId={userId}
            defaultColumnConfig={DiscountedColumnDefs}
            getData={getDiscountedServerSideData}
            configKey={K.AgGridTable.Keys.InvoiceJobDetailsDiscountedColumn}
            height={400}
            minHeight={400}
            useGlobalLoading={false}
            showTableLoadingOverlay
            useCustomLoader
          />
          <div style={{ textAlign: "left" }}>
            <Title style={{ color: "var(--blue-300)" }} level={5}>
              Others
            </Title>
          </div>

          <div
            className="ag-theme-alpine s2-theme-style"
            style={{
              height: 400,
            }}
          >
            <ServerSideDataGrid
              pagination
              paginationPageSize={5}
              userId={userId}
              defaultColumnConfig={NonDiscountedColumnDefs}
              getData={getNonDiscountedServerSideData}
              configKey={K.AgGridTable.Keys.InvoiceJobDetailsNonDiscountedColumn}
              height={400}
              minHeight={400}
              useGlobalLoading={false}
              showTableLoadingOverlay
              useCustomLoader
            />
          </div>
        </div>
      )}
    </Form>
  );
}
